import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const COUNTER_TARGET = 347;

export default function EarlyAccessHero() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const step = Math.ceil(COUNTER_TARGET / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= COUNTER_TARGET) {
        setCount(COUNTER_TARGET);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 20);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/52a325a71bcc806cf46cca29f7ca6733.jpg"
          alt="Cockpit aerial view"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060E1E]/80 via-[#060E1E]/60 to-[#060E1E]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060E1E]/60 via-transparent to-[#060E1E]/60" />
      </div>

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'linear-gradient(#00FFD1 1px, transparent 1px), linear-gradient(90deg, #00FFD1 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Corner brackets */}
      <div className="absolute top-32 left-8 w-12 h-12 border-l-2 border-t-2 border-[#00FFD1]/40" />
      <div className="absolute top-32 right-8 w-12 h-12 border-r-2 border-t-2 border-[#00FFD1]/40" />
      <div className="absolute bottom-16 left-8 w-12 h-12 border-l-2 border-b-2 border-[#00FFD1]/20" />
      <div className="absolute bottom-16 right-8 w-12 h-12 border-r-2 border-b-2 border-[#00FFD1]/20" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center pt-28 pb-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2.5 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-5 py-2 mb-8 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" />
          <span className="text-[#00FFD1] text-xs font-bold uppercase tracking-widest">
            Early Access — Limited Spots Available
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-6xl sm:text-7xl font-extrabold leading-tight mb-6 tracking-tight">
          Fly Smarter.<br />
          <span className="text-[#00FFD1]">Train Faster.</span>
        </h1>

        <p className="text-gray-300 text-xl leading-relaxed max-w-2xl mx-auto mb-10">
          WingmanFix is the AI copilot that listens, analyzes, and coaches you in real time — from your first solo to your instrument checkride.
        </p>

        {/* Stats row */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
          {[
            { value: `${count}+`, label: 'Pilots on waitlist', icon: 'ri-group-line' },
            { value: 'Free', label: 'During beta', icon: 'ri-gift-line' },
            { value: '< 2 min', label: 'To sign up', icon: 'ri-time-line' },
            { value: '100%', label: 'Data private', icon: 'ri-shield-check-line' },
          ].map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-3 bg-white/5 border border-white/10 backdrop-blur-sm rounded-xl px-5 py-3"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${s.icon} text-[#00FFD1] text-lg`} />
              </div>
              <div className="text-left">
                <div className="text-lg font-bold text-white leading-none">{s.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a
            href="#signup-form"
            className="bg-[#00FFD1] hover:bg-[#00e6bc] text-[#060E1E] font-bold px-10 py-4 rounded-lg transition-all hover:scale-105 shadow-lg shadow-[#00FFD1]/20 cursor-pointer whitespace-nowrap text-sm uppercase tracking-wider"
          >
            <span className="flex items-center gap-2">
              <i className="ri-rocket-2-line" />
              Claim Your Spot — It's Free
            </span>
          </a>
          <Link
            to="/features"
            className="border border-white/20 hover:border-[#00FFD1]/50 text-gray-300 hover:text-white font-semibold px-8 py-4 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm uppercase tracking-wider backdrop-blur-sm"
          >
            <span className="flex items-center gap-2">
              <i className="ri-play-circle-line" />
              See How It Works
            </span>
          </Link>
        </div>

        {/* Trust line */}
        <p className="mt-8 text-xs text-gray-500 flex items-center justify-center gap-2">
          <i className="ri-lock-line text-[#00FFD1]/50" />
          No credit card required &nbsp;·&nbsp; No spam &nbsp;·&nbsp; Unsubscribe anytime
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-50">
        <span className="text-xs text-gray-500 uppercase tracking-widest">Scroll</span>
        <i className="ri-arrow-down-line text-[#00FFD1] animate-bounce text-lg" />
      </div>
    </section>
  );
}
